import AddHomeIcon from '@mui/icons-material/AddHome'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import HomeIcon from '@mui/icons-material/Home'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDataFragment } from '../../generated/graphql'
import DH from '../../helpers/DH'
import log from '../../log'
import { CanBeDeleted, CanBeDeletedOrUpdated, CanBeUpdated } from '../acl/CanBe'

interface Props {
  calendar: CalendarDataFragment
  checked: boolean
  isDefault: boolean

  toggleChecked(): void

  setDefaultAction(calendar: CalendarDataFragment): void

  editAction(calendar: CalendarDataFragment): void

  deleteAction(calendar: CalendarDataFragment): void
}

const CalendarListItem: React.FC<Props> = ({
  calendar,
  checked,
  isDefault,
  toggleChecked,
  setDefaultAction,
  editAction,
  deleteAction,
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const color = DH.getColorValue(calendar.color, 600) || calendar.color

  if (!color) {
    log.log('Color not found', calendar.color, { calendar })
    return null
  }

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  function handleClose(event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation()
    setAnchorEl(null)
  }

  return (
    <>
      <Box
        onClick={toggleChecked}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? toggleChecked() : undefined)}
        sx={{
          'display': 'flex',
          'alignItems': 'center',
          'gap': 1.5,
          'px': 2,
          'py': 0.875,
          'cursor': 'pointer',
          'borderRadius': 1,
          'userSelect': 'none',
          'transition': 'background-color 0.15s',
          '&:hover': { backgroundColor: 'action.hover' },
          '&:hover .calendar-menu-btn': { opacity: 1 },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        }}
      >
        {/* Colored checkbox — filled when active, outlined when inactive */}
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '3px',
            flexShrink: 0,
            backgroundColor: checked ? color : 'transparent',
            border: `1.5px solid ${checked ? color : 'rgba(0,0,0,0.3)'}`,
            transition: 'all 0.15s',
          }}
        />

        {/* Calendar name */}
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            fontSize: '0.9rem',
            fontWeight: 400,
            color: checked ? 'text.primary' : 'text.disabled',
            transition: 'color 0.15s',
          }}
        >
          {calendar.name}
        </Typography>

        {/* Default calendar badge */}
        {isDefault && (
          <Tooltip title={t('Default calendar')} placement="top" arrow>
            <HomeIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
          </Tooltip>
        )}

        {/* Three-dot menu — visible only on hover */}
        <CanBeDeletedOrUpdated entity={calendar}>
          <IconButton
            className="calendar-menu-btn"
            size="small"
            aria-label="show actions"
            aria-controls="calendar-menu"
            aria-haspopup="true"
            onClick={handleClick}
            sx={{
              opacity: 0,
              transition: 'opacity 0.15s',
              flexShrink: 0,
              p: 0.25,
              color: 'text.secondary',
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </CanBeDeletedOrUpdated>

        <Menu id="calendar-menu" anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
          <CanBeUpdated entity={calendar}>
            <MenuItem
              onClick={(e) => {
                if (isDefault) return
                handleClose(e)
                setDefaultAction(calendar)
              }}
              dense
              disabled={isDefault}
            >
              <ListItemIcon>
                <AddHomeIcon fontSize="small" sx={{ color: isDefault ? color : undefined }} />
              </ListItemIcon>
              {isDefault ? t('Default calendar') : t('Set Default')}
            </MenuItem>
          </CanBeUpdated>
          <CanBeUpdated entity={calendar}>
            <MenuItem
              onClick={(e) => {
                handleClose(e)
                editAction(calendar)
              }}
              dense
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              {t('Edit')}
            </MenuItem>
          </CanBeUpdated>
          <CanBeDeleted entity={calendar}>
            <MenuItem
              onClick={(e) => {
                handleClose(e)
                deleteAction(calendar)
              }}
              dense
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              {t('Delete')}
            </MenuItem>
          </CanBeDeleted>
        </Menu>
      </Box>
      <Divider sx={{ mx: 2 }} />
    </>
  )
}

export default CalendarListItem
